import { inject, Injectable } from '@angular/core';
import { DashboardResponse } from '@com-delta-idp/mnx-oauth';
import {
  ANALYTICS_EVENTS,
  AnalyticsProfile,
  AnalyticsService,
  AnalyticsSiteAssets,
  ClickEventAnalytics,
  ContentBodyAbodeSupportAnalytics,
  HEADER_MENU,
  USER_LOGGED_IN_STATUS
} from '@com-delta-idp/mnx-utils';
import { map } from 'rxjs/operators';
import { AccountSummaryService } from '../../services/accountsummary/account-summary.service';

@Injectable({
  providedIn: 'root'
})
export class ContentBodyClickEventAnalyticsUtils {
  private analyticsService = inject(AnalyticsService);
  private accountSummary = inject(AccountSummaryService);
  private isLoggedInGlobal = false;
  protected contentBodyAbodeSupportAnalytics = inject(
    ContentBodyAbodeSupportAnalytics
  );

  setHomePageClickAnalytics(
    headerText: string,
    eventType: string,
    fullUrl: string,
    eventDetail: string
  ): void {
    const data: ClickEventAnalytics = {
      event: ANALYTICS_EVENTS.CLICK,
      event_detail: eventDetail,
      click: {
        name: ANALYTICS_EVENTS.RIGHT_ARROW,
        type: eventType,
        page_section: HEADER_MENU.HOMEPAGE.PAGE_SECTION,
        action: fullUrl,
        tile_name: headerText
      },
      user: {
        attributes: {
          is_logged_in: this.isLoggedInGlobal
            ? USER_LOGGED_IN_STATUS.AUTHENTICATED
            : USER_LOGGED_IN_STATUS.UNAUTHENTICATED
        }
      }
    };
    this.analyticsService.setAnalytics(data);
  }

  setHomePageClickEventAnalytics(cardDiv: Element, eventDetail: string): void {
    if (!cardDiv) return;
    cardDiv.addEventListener('click', (event) => {
      const clickedElement = event.target as HTMLElement;
      const closestAnchor = clickedElement.closest('a');
      if (!closestAnchor) return;

      event.preventDefault();

      const isComponentLink =
        closestAnchor.classList.length > 0 &&
        ((closestAnchor.classList.contains('componentLink') &&
          closestAnchor.classList.contains('block')) ||
          closestAnchor.classList.value.trim() ===
            'card-component flex-adjust  componentLink');

      const headerText = isComponentLink
        ? closestAnchor.querySelector('span,h3')?.textContent || ''
        : closestAnchor.textContent || '';

      const eventType = event.type;
      const href = closestAnchor.getAttribute('href') || '';
      const fullUrl = new URL(href, window.location.origin).href;

      this.setHomePageClickAnalytics(
        headerText,
        eventType,
        fullUrl,
        eventDetail
      );

      window.location.href = fullUrl;
    });
  }

  setHomePageSiteAssetsAnalytics(
    cardDiv: Element,
    eventDetail: string
  ): AnalyticsSiteAssets[] {
    if (!cardDiv) return;
    let position = 1;
    const site_assets_array: AnalyticsSiteAssets[] = [];

    const relevantElements = Array.from(
      cardDiv.querySelectorAll('h3, span, a')
    ).filter((element) => {
      const hasText = element.textContent?.trim();
      const isRelevantClass = [
        'card-headertxt',
        'card-title brand-head-home'
      ].includes(element.className);
      return hasText && isRelevantClass;
    });

    const newAssets = relevantElements.map((element) => ({
      name: element.textContent?.trim() || '',
      type: eventDetail,
      position: `${position++}`
    }));

    site_assets_array.push(...newAssets);
    return site_assets_array;
  }

  setHomePageLoadAssetAnalytics(site_assets: AnalyticsSiteAssets[]): void {
    this.accountSummary
      .getAccountSummaryDetails()
      .pipe(
        map((response: DashboardResponse) => {
          if (!response || Object.keys(response).length === 0) {
            return {
              profile: null as AnalyticsProfile | null,
              isWarmState: false
            };
          }
          this.isLoggedInGlobal = response.loggedIn;
          const getValueOrEmpty = (
            value: string | undefined | null
          ): string => {
            return value === undefined || value === null ? '' : value;
          };
          return {
            ...response,
            profile: {
              skymiles_number: getValueOrEmpty(response?.smNumber),
              skymiles_tier: getValueOrEmpty(response?.membershipLevel),
              amex_card_type: getValueOrEmpty(response?.smeCardTypes),
              available_miles: getValueOrEmpty(response?.smBalance),
              email: getValueOrEmpty(response?.primaryEmailAddress),
              email_domain: getValueOrEmpty(
                response?.primaryEmailAddress?.split('@')?.[1]
              )
            } as AnalyticsProfile
          };
        })
      )
      .subscribe(({ profile, isWarmState }) => {
        this.contentBodyAbodeSupportAnalytics.populateHomePageAssets(
          site_assets,
          isWarmState,
          profile,
          this.isLoggedInGlobal
        );
      });
  }
}
